import {
  Briefcase,
  Building2,
  CalendarClock,
  ClipboardCheck,
  CloudUpload,
  FileLock,
  FileText,
  FolderOpen,
  KeyRound,
  LayoutDashboard,
  Link2,
  ListChecks,
  Monitor,
  Plug,
  Settings,
  Settings2,
  UserCog,
  Users,
} from 'lucide-react';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';

interface FirmSidebarProps {
  firmId: string;
}

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

export const FirmSidebar: React.FC<FirmSidebarProps> = ({ firmId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const getActiveTab = () => {
    if (currentPath.includes(`/firms/${firmId}/details`)) return 'details';
    if (currentPath.includes(`/firms/${firmId}/edit`)) return 'edit';
    if (currentPath.includes(`/firms/${firmId}/sop`)) return 'sop';
    if (currentPath.includes(`/firms/${firmId}/demand-configurations`))
      return 'demand-configurations';
    if (currentPath.includes(`/firms/${firmId}/proofreading`))
      return 'proofreading';
    if (
      currentPath.includes(`/firms/${firmId}/hipaa-cover-sheet`) ||
      currentPath.includes(`/firms/${firmId}/templates`)
    )
      return 'templates';
    if (currentPath.includes(`/firms/${firmId}/attorneys`)) return 'attorneys';
    if (currentPath.includes(`/firms/${firmId}/role-assignments`))
      return 'role-assignments';
    if (currentPath.includes(`/firms/${firmId}/intake-preferences`))
      return 'intake-preferences';
    if (currentPath.includes(`/firms/${firmId}/onepassword`))
      return 'onepassword';
    if (currentPath.includes(`/firms/${firmId}/filevine-sections`))
      return 'filevine-sections';
    if (currentPath.includes(`/firms/${firmId}/filevine-organization`))
      return 'filevine-organization';
    if (currentPath.includes(`/firms/${firmId}/services`)) return 'services';
    if (currentPath.includes(`/firms/${firmId}/integrations`))
      return 'integrations';
    if (currentPath.includes(`/firms/${firmId}/document-sync`))
      return 'document-sync';
    if (currentPath.includes(`/firms/${firmId}/cms-features`))
      return 'cms-features';
    if (currentPath.includes(`/firms/${firmId}/task-linking`))
      return 'task-linking';
    if (currentPath.includes(`/firms/${firmId}/client-portal`))
      return 'client-portal';
    if (currentPath.includes(`/firms/${firmId}/task-preferences`))
      return 'task-preferences';
    return 'overview';
  };

  const activeTab = getActiveTab();

  const navGroups: NavGroup[] = [
    {
      label: 'Firm Overview',
      items: [
        {
          id: 'overview',
          label: 'Overview',
          path: `/firms/${firmId}`,
          icon: <LayoutDashboard className="h-4 w-4" />,
        },
        {
          id: 'details',
          label: 'Details',
          path: `/firms/${firmId}/details`,
          icon: <FileText className="h-4 w-4" />,
        },
        {
          id: 'attorneys',
          label: 'Attorneys',
          path: `/firms/${firmId}/attorneys`,
          icon: <Users className="h-4 w-4" />,
        },
        {
          id: 'role-assignments',
          label: 'Role Assignments',
          path: `/firms/${firmId}/role-assignments`,
          icon: <UserCog className="h-4 w-4" />,
        },
        {
          id: 'sop',
          label: 'Firm SOP',
          path: `/firms/${firmId}/sop`,
          icon: <FileText className="h-4 w-4" />,
        },
      ],
    },
    {
      label: 'Case Intake',
      items: [
        {
          id: 'intake-preferences',
          label: 'Intake Preferences',
          path: `/firms/${firmId}/intake-preferences`,
          icon: <ListChecks className="h-4 w-4" />,
        },
        {
          id: 'services',
          label: 'Scope of Work',
          path: `/firms/${firmId}/services`,
          icon: <Briefcase className="h-4 w-4" />,
        },
      ],
    },
    {
      label: 'Document Generation',
      items: [
        {
          id: 'demand-configurations',
          label: 'Demands',
          path: `/firms/${firmId}/demand-configurations`,
          icon: <Settings className="h-4 w-4" />,
        },
        {
          id: 'proofreading',
          label: 'Proofreading',
          path: `/firms/${firmId}/proofreading`,
          icon: <ClipboardCheck className="h-4 w-4" />,
        },
        {
          id: 'templates',
          label: 'Templates',
          path: `/firms/${firmId}/templates`,
          icon: <FileLock className="h-4 w-4" />,
        },
      ],
    },
    {
      label: 'Integrations',
      items: [
        {
          id: 'onepassword',
          label: 'OnePassword',
          path: `/firms/${firmId}/onepassword`,
          icon: <KeyRound className="h-4 w-4" />,
        },
        {
          id: 'integrations',
          label: 'OAuth Integrations',
          path: `/firms/${firmId}/integrations`,
          icon: <Plug className="h-4 w-4" />,
        },
        {
          id: 'filevine-sections',
          label: 'Filevine Sections',
          path: `/firms/${firmId}/filevine-sections`,
          icon: <FolderOpen className="h-4 w-4" />,
        },
        {
          id: 'filevine-organization',
          label: 'Filevine Organization',
          path: `/firms/${firmId}/filevine-organization`,
          icon: <Building2 className="h-4 w-4" />,
        },
      ],
    },
    {
      label: 'Platform',
      items: [
        {
          id: 'document-sync',
          label: 'Document Sync',
          path: `/firms/${firmId}/document-sync`,
          icon: <CloudUpload className="h-4 w-4" />,
        },
        {
          id: 'cms-features',
          label: 'CMS Features',
          path: `/firms/${firmId}/cms-features`,
          icon: <Settings2 className="h-4 w-4" />,
        },
        {
          id: 'task-linking',
          label: 'Task Linking',
          path: `/firms/${firmId}/task-linking`,
          icon: <Link2 className="h-4 w-4" />,
        },
        {
          id: 'task-preferences',
          label: 'Task Preferences',
          path: `/firms/${firmId}/task-preferences`,
          icon: <CalendarClock className="h-4 w-4" />,
        },
        {
          id: 'client-portal',
          label: 'Secure Upload',
          path: `/firms/${firmId}/client-portal`,
          icon: <Monitor className="h-4 w-4" />,
        },
      ],
    },
  ];

  return (
    <nav className="w-56 shrink-0 border-r border-gray-200 bg-gray-50/50 overflow-y-auto">
      <div className="flex flex-col gap-1 py-3">
        {navGroups.map((group) => (
          <div key={group.label}>
            <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              {group.label}
            </div>
            {group.items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => navigate(item.path)}
                className={cn(
                  'flex w-full items-center gap-2.5 px-4 py-2 text-sm font-medium transition-colors',
                  activeTab === item.id
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                )}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>
        ))}
      </div>
    </nav>
  );
};
